import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Placeholder from 'flarum/common/components/Placeholder';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import Icon from 'flarum/common/components/Icon';
import sortTags from 'ext:flarum/tags/common/utils/sortTags';
import TagChip from './TagChip';
/**
 * A `q` value that matches nothing, so DiscussionListItem renders no search
 * excerpt for these results.
 */
const randomStr = Math.pow(Math.random() + 10, 100).toString(36);
export default class RelatedDiscussionsList extends Component {
  related = [];
  loading = false;
  sticky = false;
  tagScopes = [];

  /**
   * Whether the reader has toggled a chip. Until they do, the selection is
   * just the admin-configured starting scope and only weights the ranking;
   * an explicit toggle is a stronger signal and hard-filters instead.
   */
  scopeTouched = false;

  /** Defers the first search until the widget nears the viewport. */

  /** The first search has been issued; later ones come from tag toggles. */
  searched = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.attrs.setInstance(this);
    let tags = this.discussionTags();

    // Filter to primary tags by default only if setting is false
    if (!app.forum.attribute('glowingblue-related.enable_all_tags_by_default')) {
      tags = tags.filter(tag => tag.isPrimary());
    }
    this.tagScopes = tags.map(tag => tag.slug());
  }
  oncreate(vnode) {
    super.oncreate(vnode);

    // The widget sits at the end of the stream, which on a long discussion is
    // thousands of pixels below the fold. Searching on mount spent a query on
    // results nobody had scrolled to; wait until it is actually approaching.
    if (typeof IntersectionObserver === 'undefined') {
      this.searchOnce();
      return;
    }
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.searchOnce();
      }
    },
    // Start a little early so results are there by the time it is read.
    {
      rootMargin: '200px'
    });
    this.observer.observe(vnode.dom);
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.observer?.disconnect();
    this.observer = undefined;
    this.hide();
    this.attrs.setInstance(undefined);
  }
  show() {
    // The link can be clicked long before the widget scrolls into view.
    this.searchOnce();
    const element = this.element;
    if (!element) return;
    const rect = element.getBoundingClientRect();

    // At least 10px visible
    const isVisible = rect.top >= 0 && rect.top + 10 <= window.innerHeight;
    if (isVisible) {
      window.scrollTo({
        top: rect.top + window.scrollY - 10,
        behavior: 'smooth'
      });
    } else {
      this.sticky = true;
      document.body.classList.add('related-discussions-list-open');
      m.redraw();
    }
  }
  hide() {
    this.sticky = false;
    document.body.classList.remove('related-discussions-list-open');
    m.redraw();
  }
  view() {
    return m("div", {
      className: "container"
    }, m("div", {
      className: "RelatedDiscussionsList",
      id: "related-discussion-list"
    }, m("div", {
      className: `DiscussionPage-stream ${this.sticky ? 'container sticky' : ''}`
    }, this.sticky && m(Button, {
      icon: "fas fa-times",
      onclick: () => this.hide(),
      className: "Button Button--icon Button--link CloseButton"
    }), m("h3", {
      className: "RelatedDiscussionsList-title"
    }, app.translator.trans('glowingblue-related.forum.related.title')), m("div", {
      className: "RelatedDiscussionsList-tags"
    }, m("label", {
      className: "RelatedDiscussionsList-tags--label"
    }, app.translator.trans('glowingblue-related.forum.related.tag_scopes_label')), this.getTagChips()), this.loading && m(LoadingIndicator, null), !this.loading && m("div", {
      className: "RelatedDiscussionsList-discussions"
    }, !this.related.length && m(Placeholder, {
      text: app.translator.trans('glowingblue-related.forum.related.none_found')
    }), this.related.map(discussion => m(DiscussionListItem, {
      discussion: discussion,
      params: {
        q: randomStr
      }
    }))), !!this.related.length && m(Link, {
      href: app.route('index', {
        q: this.qParam
      }),
      className: "RelatedDiscussionsList-search"
    }, m(Icon, {
      name: "fas fa-search"
    }), " ", app.translator.trans('glowingblue-related.forum.related.search_more')))));
  }

  /**
   * Gambit syntax for the "open search" link. Gambits are a frontend concern
   * in 2.x: the search page parses `related:<id>` back into
   * filter[related_to], so the full results carry the same shared-tag ranking
   * as the widget — and exclude the source discussion, which a title search
   * returned as its own top hit.
   */
  get qParam() {
    return `related:${this.attrs.discussion.id()}`;
  }
  get params() {
    const {
      discussion
    } = this.attrs;

    // related_to scores candidates by shared-tag overlap and orders by that
    // score, so results come back ranked by relevance. It is an indexed
    // lookup over the discussion_tag pivot; sending filter[q] here instead
    // made every discussion view run two MATCH ... AGAINST fulltext scans
    // over posts.content (the search, then the same search again for the
    // pagination total the widget never uses).
    const filter = {
      related_to: String(discussion.id())
    };

    // The default scope only weights the ranking — related_to already scores
    // by shared-tag overlap, so a discussion under a different primary tag
    // can still surface if it shares more tags. Once the reader toggles a
    // chip they have asked for something narrower, so the selection becomes
    // a hard filter.
    if (this.scopeTouched && this.tagScopes.length) {
      filter.tag = this.tagScopes.join(',');
    }
    return {
      filter,
      page: {
        limit: 4
      }
    };
  }

  /**
   * `tags()` is `false` until the relationship is loaded, and its array may
   * contain holes for tags not present in the store.
   */
  discussionTags() {
    return (this.attrs.discussion.tags() || []).filter(tag => !!tag);
  }
  getTagChips() {
    return sortTags(this.discussionTags()).map(tag => this.getTagChip(tag));
  }
  getTagChip(tag) {
    return m(TagChip, {
      _tag: tag,
      mode: this.tagScopes.includes(tag.slug()) ? 'remove' : 'add',
      onAdd: () => {
        this.scopeTouched = true;
        this.tagScopes.push(tag.slug());
        m.redraw();
        this.search();
      },
      onRemove: () => {
        this.scopeTouched = true;
        this.tagScopes = this.tagScopes.filter(_slug => _slug !== tag.slug());
        m.redraw();
        this.search();
      }
    });
  }

  /**
   * Search once, on first reveal. The observer can fire repeatedly and show()
   * calls this too, so later invocations are no-ops; a tag toggle calls
   * search() directly to re-query.
   */
  searchOnce() {
    if (this.searched) return Promise.resolve();
    this.searched = true;
    return this.search();
  }
  search() {
    // Nothing to match on: either the discussion carries no tags at all, or
    // the reader has deselected every chip. related_to would return nothing
    // in the first case and the request is pointless in the second, so skip
    // it entirely rather than ask the server.
    if (!this.discussionTags().length || this.scopeTouched && !this.tagScopes.length) {
      this.loading = false;
      this.related = [];
      m.redraw();
      return Promise.resolve();
    }
    this.loading = true;
    return app.store.find('discussions', this.params).then(results => {
      this.loading = false;
      this.related = results;
      m.redraw();
    });
  }
}
flarum.reg.add('glowingblue-related', 'forum/components/RelatedDiscussionsList', RelatedDiscussionsList);